import {NextRouter, useRouter} from 'next/router';
import {useCallback, useMemo, useRef, useState} from 'react';
import {PromotionBannerContainer} from '../../../../common/containers/PromotionBannerContainer';
import {StaticLandingRankingMoreFetcher} from '../../../../common/fetchers/StaticLandingRankingMoreFetcher';
import {t} from '../../../../common/getTranslations';
import {isGreaterThanZero} from '../../../../common/isGreaterThanZero';
import {requestContext} from '../../../../common/requestContext';
import {CategoryTypeVo} from '../../../../common/shared/api/nicolas/vo/enums/CategoryTypeVo.enum';
import useTypedSelector from '../../../../common/useTypedSelector';
import {IconType} from '../../../../design-system/atoms/Icon/IconType.enum';
import {MainRankingGenreTab} from '../../../../design-system/organisms/MainRankingGenreTab/MainRankingGenreTab';
import {NoItem} from '../../../../design-system/organisms/NoItem/NoItem';
import {TitleBar} from '../../../../design-system/organisms/TitleBar/TitleBar';
import {TitleBarType} from '../../../../design-system/organisms/TitleBar/TitleBarType.enum';
import {StaticLandingRanking as StaticLandingRankingPage} from '../../../../design-system/pages/StaticLanding/StaticLandingRanking/StaticLandingRanking';
import {EMPTY_LANDING_PRODUCT_TEXT_KEY} from '../../../../domain/landing/constant';
import {
  StaticLandingRankingSection,
  StaticLandingRankingSectionParam,
} from '../../../../graphql/generated/types';
import {PageViewCustomPropsDataKey} from '../../../../logging/PageViewCustomPropsDataKey.enum';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {showRankingHelpDialog} from '../../../../main/showRankingHelpDialog';
import {STATIC_LANDING_RANKING_CATEGORY_LIST} from '../../../../staticLanding/ranking/constants';
import {selectIsSubtab} from '../../../../staticLanding/selector';
import {GnbMenuId} from '../../../menu/constant';
import {CommonSectionProps} from '../../type';

export function StaticLandingRanking({section, children}: CommonSectionProps) {
  const {isSubtab: _isSubtab, isPreferTab: _isPreferTab} = useTypedSelector(
    state => ({
      isSubtab: selectIsSubtab({section, state}),
      isPreferTab: state.menu.selectedGnbMenuId === GnbMenuId.Prefer,
    })
  );
  const {isMobile, isWebView} = requestContext.requestInfo.display;

  const isSubtabRef = useRef(_isSubtab);
  const isPreferTabRef = useRef(_isPreferTab);
  const isSubtab = isSubtabRef.current;
  const isPreferTab = isPreferTabRef.current;

  const staticLandingRankingSection = section as Omit<
    StaticLandingRankingSection,
    'groups'
  >;
  const {
    rankingTime,
    rankingTypeList,
    subcategoryList,
    param,
    displayAd,
    totalCount,
    title,
    categoryTabList,
    promotionBanner,
    withOperationArea,
  } = staticLandingRankingSection;

  const router = useRouter();
  const [isShowSubcategoryDialog, setIsShowSubcategoryDialog] = useState(false);
  const [isShowRankingTypeDialog, setIsShowRankingTypeDialog] = useState(false);

  const currentCategoryName = useMemo(
    () =>
      categoryTabList.find(
        category => category.param === String(param.categoryUid)
      )?.name,
    [categoryTabList, param.categoryUid]
  );
  const currentSubcategoryName = useMemo(
    () =>
      subcategoryList?.find(item => item.param === param.subcategory?.param)
        ?.name,
    [param.subcategory?.param, subcategoryList]
  );
  const currentRankingTypeName = useMemo(
    () =>
      rankingTypeList?.find(item => item.param === param.rankingType?.param)
        ?.name,
    [param.rankingType?.param, rankingTypeList]
  );

  const onClickSubcategoryItem = useCallback(
    (subcategoryUid: string) => {
      setIsShowSubcategoryDialog(false);
      if (param.subcategory?.param !== subcategoryUid) {
        routeWithParam({router, param, subcategoryUid, isSubtab});
      }
    },
    [isSubtab, param, router]
  );

  const onClickRankingTypeItem = useCallback(
    (rankingType: string) => {
      setIsShowRankingTypeDialog(false);
      if (rankingType !== param.rankingType?.param) {
        routeWithParam({
          router,
          param,
          subcategoryUid: param.subcategory?.param,
          rankingType,
          isSubtab,
        });
      }
    },
    [isSubtab, param, router]
  );

  const onClickCategoryItem = useCallback(
    (categoryUid: string) => {
      if (categoryUid !== String(param.categoryUid)) {
        routeWithParam({
          router,
          param,
          categoryUid: Number(categoryUid),
          isSubtab,
        });
      }
    },
    [isSubtab, param, router]
  );

  if (isSubtab) {
    return (
      <div
        className="flex w-full grow flex-col"
        {...{
          [PageViewCustomPropsDataKey.TabType]: withOperationArea
            ? 'tab_type=landing_multi'
            : 'tab_type=landing_single',
        }}
        {...putTiaraObjectProps({
          customProps: {
            page_category_filter: currentCategoryName,
            page_genre_filter: currentSubcategoryName,
            page_sort: currentRankingTypeName,
          },
          click: {
            layer1: '섹션',
          },
        })}
      >
        {promotionBanner && (
          <PromotionBannerContainer promotionBanner={promotionBanner} />
        )}
        {isPreferTab && withOperationArea && (
          <TitleBar title={title ?? undefined} type={TitleBarType.Main}>
            <MainRankingGenreTab
              isMobile={isMobile}
              rankingGenreItemList={categoryTabList}
              selectedItemParam={String(param.categoryUid)}
              dateStr={rankingTime ?? undefined}
              onClickTabItem={onClickCategoryItem}
              {...{
                [PageViewCustomPropsDataKey.PageCategoryFilter]:
                  param.categoryUid === CategoryTypeVo.Cartoon
                    ? 'page_category_filter=웹툰'
                    : 'page_category_filter=웹소설',
              }}
            />
          </TitleBar>
        )}
        {isPreferTab && !withOperationArea && (
          <MainRankingGenreTab
            isMobile={isMobile}
            className="mb-4pxr"
            rankingGenreItemList={categoryTabList}
            selectedItemParam={String(param.categoryUid)}
            dateStr={rankingTime ?? undefined}
            onClickTabItem={onClickCategoryItem}
            {...{
              [PageViewCustomPropsDataKey.PageCategoryFilter]:
                param.categoryUid === CategoryTypeVo.Cartoon
                  ? 'page_category_filter=웹툰'
                  : 'page_category_filter=웹소설',
            }}
          />
        )}
        {!isPreferTab && (
          <TitleBar
            title={title ?? undefined}
            type={TitleBarType.Landing}
            label={rankingTime ?? undefined}
            icon={rankingTime ? IconType.ButtonHelpS : undefined}
            onClickLabel={() => {
              showRankingHelpDialog({isMobile});
            }}
          />
        )}

        {isGreaterThanZero(totalCount) && (
          <StaticLandingRankingMoreFetcher
            section={staticLandingRankingSection}
          >
            <div className="flex w-full grow flex-col">{children}</div>
          </StaticLandingRankingMoreFetcher>
        )}
        {!isGreaterThanZero(totalCount) && (
          <NoItem isMobile={isMobile}>
            {t(EMPTY_LANDING_PRODUCT_TEXT_KEY)}
          </NoItem>
        )}
      </div>
    );
  } else {
    return (
      <div
        {...putTiaraObjectProps({
          customProps: {
            page_category_filter: currentCategoryName,
            page_genre_filter: currentSubcategoryName,
            page_sort: currentRankingTypeName,
          },
          click: {
            layer1: '섹션',
          },
        })}
      >
        <StaticLandingRankingPage
          isMobile={isMobile}
          isWebView={isWebView}
          rankingTime={rankingTime}
          categoryList={STATIC_LANDING_RANKING_CATEGORY_LIST}
          currentCategoryUid={param.categoryUid}
          rankingTypeList={rankingTypeList}
          currentRankingType={param.rankingType?.param}
          subcategoryList={subcategoryList}
          currentSubcategoryUid={param.subcategory?.param}
          currentCategoryName={currentCategoryName}
          currentSubcategoryName={currentSubcategoryName}
          currentRankingTypeName={currentRankingTypeName}
          isShowSubcategoryDialog={isShowSubcategoryDialog}
          isShowRankingTypeDialog={isShowRankingTypeDialog}
          showSubcategoryDialog={() => setIsShowSubcategoryDialog(true)}
          hideSubcategoryDialog={() => setIsShowSubcategoryDialog(false)}
          showRankingTypeDialog={() => setIsShowRankingTypeDialog(true)}
          hideRankingTypeDialog={() => setIsShowRankingTypeDialog(false)}
          onClickSubcategoryItem={onClickSubcategoryItem}
          onClickRankingTypeItem={onClickRankingTypeItem}
          displayAd={displayAd}
          {...putTiaraObjectProps({
            click: {
              layer1: '섹션',
            },
          })}
        >
          {isGreaterThanZero(totalCount) && (
            <StaticLandingRankingMoreFetcher
              section={staticLandingRankingSection}
            >
              {children}
            </StaticLandingRankingMoreFetcher>
          )}
          {!isGreaterThanZero(totalCount) && (
            <NoItem isMobile={isMobile}>
              {t(EMPTY_LANDING_PRODUCT_TEXT_KEY)}
            </NoItem>
          )}
        </StaticLandingRankingPage>
      </div>
    );
  }
}

function routeWithParam({
  router,
  param,
  categoryUid: _categoryUid,
  subcategoryUid,
  rankingType,
  isSubtab,
}: {
  router: NextRouter;
  param: StaticLandingRankingSectionParam;
  categoryUid?: number;
  subcategoryUid?: string;
  rankingType?: string;
  isSubtab: boolean;
}) {
  const {categoryUid} = param;
  let pathname, query;
  if (isSubtab) {
    // /menu/{menu_uid}/screen/{screen_uid}
    pathname = window.location.pathname;
    // 서브탭인 경우 카테고리 uid를 변경하는 경우는 존재하지 않음
    query = {
      category_uid: _categoryUid ?? categoryUid,
    };
  } else {
    pathname = `/landing/ranking/${_categoryUid ?? categoryUid}`;
    if (subcategoryUid !== undefined) {
      pathname += `/${subcategoryUid}`;
    }
    query = {
      ...(rankingType && {
        ranking_type: rankingType,
      }),
    };
  }

  router.push(
    {
      pathname,
      query,
    },
    undefined,
    {scroll: false}
  );
}
